package com.schoolconnect.app.repository.timetable

import com.schoolconnect.app.datasource.network.KtorApiClient
import com.schoolconnect.app.datasource.network.response.TimetableClassHeaderResponse
import com.schoolconnect.app.datasource.network.response.ParentTimetableWeekResponse
import com.schoolconnect.app.datasource.network.request.TimetableCombinedCreateRequest
import com.schoolconnect.app.datasource.network.request.toUpdateRequest
import com.schoolconnect.app.datasource.network.response.TimetableViewResponse
import com.schoolconnect.app.datasource.network.response.TeacherTimetableClassResponse
import com.schoolconnect.app.datasource.network.response.WeeklyTimetableResponse
import com.schoolconnect.app.util.scLog
import com.schoolconnect.app.repository.common.RepositoryCache
import com.schoolconnect.app.repository.common.loadPagedItems
import com.schoolconnect.app.repository.common.tenantCacheKey
import kotlinx.coroutines.CoroutineScope

data class TimetablePageResult(
    val items: List<TimetableClassHeaderResponse>,
    val totalCount: Int
)

object TimetableRepository {
    private const val TAG = "[DATA_RETRIEVE_SC] TimetableRepository"
    private const val ALL_TIMETABLES_PAGE_SIZE = 100
    private const val ALL_TIMETABLES_MAX_PAGES = 100
    private val pageCache = RepositoryCache<TimetablePageResult>()
    private val allCache = RepositoryCache<List<TimetableClassHeaderResponse>>()
    private val parentCache = RepositoryCache<ParentTimetableWeekResponse>()

    suspend fun loadTeacherClasses(): Result<List<TeacherTimetableClassResponse>> =
        KtorApiClient.getTeacherTimetableClasses().onSuccess { scLog("$TAG loadTeacherClasses success count=${it.size}") }
            .onFailure { scLog("$TAG loadTeacherClasses failure", it) }

    suspend fun loadTeacherWeek(classSectionId: String): Result<WeeklyTimetableResponse> {
        val id = classSectionId.trim()
        if (id.isBlank()) return Result.failure(IllegalArgumentException("A class section is required to load its timetable."))
        scLog("$TAG loadTeacherWeek start classSectionId=$id")
        return KtorApiClient.getTeacherTimetable(id).onSuccess {
            scLog("$TAG loadTeacherWeek success classSectionId=$id regularSlots=${it.regularSlots.orEmpty().size} specialSlots=${it.specialSlots.orEmpty().size}")
        }.onFailure { scLog("$TAG loadTeacherWeek failure classSectionId=$id", it) }
    }

    suspend fun create(request: TimetableCombinedCreateRequest): Result<TimetableViewResponse> {
        scLog("$TAG create start classSectionId=${request.classSectionId} slotCount=${request.slots.size} cellCount=${request.cells.size}")
        return KtorApiClient.createTimetable(request).onSuccess {
            pageCache.clear()
            allCache.clear()
            scLog("$TAG create success classSectionId=${request.classSectionId}")
        }.onFailure { scLog("$TAG create failure classSectionId=${request.classSectionId}", it) }
    }

    suspend fun update(headerId: String, request: TimetableCombinedCreateRequest): Result<TimetableViewResponse> {
        val normalizedId = headerId.trim()
        if (normalizedId.isBlank()) return Result.failure(IllegalArgumentException("A timetable header is required for editing."))
        scLog("$TAG update start headerId=$normalizedId classSectionId=${request.classSectionId} slotCount=${request.slots.size} cellCount=${request.cells.size}")
        return KtorApiClient.updateTimetable(normalizedId, request.toUpdateRequest()).onSuccess {
            pageCache.clear()
            allCache.clear()
            scLog("$TAG update success headerId=$normalizedId")
        }.onFailure { scLog("$TAG update failure headerId=$normalizedId", it) }
    }

    suspend fun load(headerId: String): Result<TimetableViewResponse> {
        val normalizedId = headerId.trim()
        if (normalizedId.isBlank()) return Result.failure(IllegalArgumentException("A timetable header is required for viewing."))
        scLog("$TAG load view headerId=$normalizedId")
        return KtorApiClient.getTimetable(normalizedId)
    }

    suspend fun loadPage(
        classSectionId: String? = null,
        pageIndex: Int = 0,
        pageSize: Int = 20,
        sortBy: String = "",
        ascending: Boolean = true,
        refreshScope: CoroutineScope
    ): Result<TimetablePageResult> {
        val safePageIndex = pageIndex.coerceAtLeast(0)
        val safePageSize = pageSize.coerceIn(1, 100)
        val safeOffset = safePageIndex * safePageSize
        scLog("$TAG loadPage start pageIndex=$safePageIndex pageSize=$safePageSize offset=$safeOffset")
        val normalizedClassSectionId = classSectionId?.trim()?.takeIf { it.isNotBlank() }
        val normalizedSort = sortBy.trim().takeIf { it in setOf("class_name", "section_name", "status", "created_at", "updated_at") }
        val key = tenantCacheKey("timetables:$normalizedClassSectionId:$safeOffset:$safePageSize:$normalizedSort:$ascending")
        return pageCache.load(refreshScope, TAG, "loadPage", key) {
            KtorApiClient.listTimetables(
                classSectionId = normalizedClassSectionId,
                limit = safePageSize,
                offset = safeOffset,
                sortBy = normalizedSort,
                order = if (ascending) "asc" else "desc"
            ).map { response -> TimetablePageResult(response.items, response.count) }
                .onSuccess { scLog("$TAG loadPage success count=${it.items.size} total=${it.totalCount}") }
                .onFailure { scLog("$TAG loadPage failure pageIndex=$safePageIndex pageSize=$safePageSize", it) }
        }
    }

    suspend fun loadAll(refreshScope: CoroutineScope): Result<List<TimetableClassHeaderResponse>> {
        val key = tenantCacheKey("timetables:all")
        return allCache.load(refreshScope, TAG, "loadAll", key) {
            runCatching {
                loadPagedItems(
                    tag = TAG,
                    operation = "loadAll",
                    pageSize = ALL_TIMETABLES_PAGE_SIZE,
                    maxPages = ALL_TIMETABLES_MAX_PAGES,
                    startContext = "all"
                ) { offset ->
                    KtorApiClient.listTimetables(
                        limit = ALL_TIMETABLES_PAGE_SIZE,
                        offset = offset,
                        sortBy = "class_name",
                        order = "asc"
                    ).getOrElse { throw it }.items
                }
            }
        }
    }

    suspend fun loadParentWeek(studentId: String, weekStart: String? = null, refreshScope: CoroutineScope? = null): Result<ParentTimetableWeekResponse> {
        val normalizedStudentId = studentId.trim()
        if (normalizedStudentId.isBlank()) return Result.failure(IllegalArgumentException("A linked student is required."))
        val normalizedWeekStart = weekStart?.trim()?.takeIf { it.isNotBlank() }
        val key = tenantCacheKey("parent-timetable:$normalizedStudentId:${normalizedWeekStart.orEmpty()}")
        return refreshScope?.let { scope -> parentCache.load(scope, TAG, "loadParentWeek", key) { KtorApiClient.getParentTimetable(normalizedStudentId, normalizedWeekStart) } }
            ?: KtorApiClient.getParentTimetable(normalizedStudentId, normalizedWeekStart)
    }
}
